import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Partage des applications aux vendeurs d'une marketplace
//
// Le marchand propriétaire installe une app depuis son dashboard, puis
// peut la « partager » avec ses vendeurs :
//   • partage global  → tous les vendeurs y ont accès
//   • partage ciblé   → seuls certains vendeurs (table appvendeur_acces)
//
// Le vendeur retrouve les apps partagées dans son espace vendeur et les
// utilise pour son propre compte (ex. ZCalendar pour ses réservations).
// ─────────────────────────────────────────────────────────────

export interface SharedApp {
  /** Ligne d'installation dans appvendeur. */
  appvendeurId: number;
  idapp: number;
  nom: string;
  description: string;
  nompage: string;
  image: string;
  icon: string;
  /** true = partagée avec tous les vendeurs. */
  tous: boolean;
  /** Limite d'utilisation par vendeur (0 = illimité). Ex. 1 calendrier par vendeur. */
  limiteVendeur: number;
  /** Condition à remplir pour obtenir davantage (texte libre de la marketplace). */
  conditionPlus: string;
}

export interface AppSharingState {
  shared: boolean;
  all: boolean;
  vendorIds: number[];
  limiteVendeur: number;
  conditionPlus: string;
}

/** Icône/label de repli par page d'app (miroir de l'AppStore). */
export function appIconFor(nompage: string): string {
  const map: Record<string, string> = {
    'analytics-advanced': 'ri-line-chart-line',
    'customers-manager': 'ri-user-settings-line',
    'whatsapp-button': 'ri-whatsapp-line',
    'ai-shopper': 'ri-robot-2-line',
    'customer-support': 'ri-customer-service-2-line',
    'facebook-pixel': 'ri-facebook-circle-line',
    'ia-reporting': 'ri-brain-line',
    'ma-fidelite': 'ri-heart-line',
    'fidelite-recompenses': 'ri-vip-crown-line',
    'mon-assistant': 'ri-robot-2-line',
    paypal: 'ri-paypal-line',
    'file-manager': 'ri-folder-line',
    forms: 'ri-survey-line',
    zcalendar: 'ri-calendar-line',
    zifekbi: 'ri-bar-chart-box-line',
    gestionpro: 'ri-projector-line',
    zcall: 'ri-vidicon-line',
    formation: 'ri-graduation-cap-line',
    hebergement: 'ri-hotel-line',
    finance: 'ri-funds-line',
    marketplace: 'ri-store-3-line',
    banners: 'ri-image-2-line',
    products: 'ri-shopping-bag-3-line',
    services: 'ri-service-line',
    orders: 'ri-file-list-3-line',
  };
  return map[nompage] || 'ri-apps-2-line';
}

interface AppvendeurRow {
  id: number;
  idapp: number;
  nomapp: string | null;
  nompage: string | null;
  status: number | null;
  partage_vendeurs: number | null;
  partage_tous: number | null;
  limite_vendeur: number | null;
  condition_plus: string | null;
}

interface AppstoreRow {
  id: number;
  nom: string | null;
  description: string | null;
  nompage: string | null;
  image: string | null;
  typeapp: string | null;
}

/** Apps installées par la marketplace, avec leur état de partage. */
export async function fetchSharingApps(marketplaceId: number): Promise<(AppvendeurRow & {
  nom: string;
  description: string;
  image: string;
  icon: string;
  sharedCount: number;
})[]> {
  if (!marketplaceId) return [];
  try {
    const { data: installed, error } = await supabase
      .from('appvendeur')
      .select('id, idapp, nomapp, nompage, status, partage_vendeurs, partage_tous, limite_vendeur, condition_plus')
      .eq('idcommerce', marketplaceId)
      .order('id', { ascending: false });
    if (error || !installed) return [];
    const rows = installed as AppvendeurRow[];
    if (rows.length === 0) return [];

    const appIds = [...new Set(rows.map((r) => Number(r.idapp)).filter((n) => Number.isFinite(n)))];
    const { data: apps } = await supabase
      .from('appstore')
      .select('id, nom, description, nompage, image, typeapp')
      .in('id', appIds);
    const byId = new Map<number, AppstoreRow>();
    (apps as AppstoreRow[] | null)?.forEach((a) => byId.set(Number(a.id), a));

    // Nombre de vendeurs explicitement autorisés (partage ciblé).
    const appvendeurIds = rows.map((r) => Number(r.id));
    const countMap = new Map<number, number>();
    if (appvendeurIds.length > 0) {
      const { data: accesRows } = await supabase
        .from('appvendeur_acces')
        .select('idappvendeur')
        .in('idappvendeur', appvendeurIds);
      (accesRows as { idappvendeur: number }[] | null)?.forEach((a) => {
        const k = Number(a.idappvendeur);
        countMap.set(k, (countMap.get(k) || 0) + 1);
      });
    }

    return rows.map((r) => {
      const meta = byId.get(Number(r.idapp));
      const nompage = r.nompage || meta?.nompage || '';
      return {
        ...r,
        nom: meta?.nom || r.nomapp || `App #${r.idapp}`,
        description: meta?.description || '',
        image: meta?.image || '',
        icon: appIconFor(nompage),
        sharedCount: countMap.get(Number(r.id)) || 0,
      };
    });
  } catch {
    return [];
  }
}

/** Lit l'état de partage exact d'une app (global + vendeurs sélectionnés). */
export async function fetchAppSharingState(appvendeurId: number): Promise<AppSharingState> {
  try {
    const { data: row } = await supabase
      .from('appvendeur')
      .select('partage_vendeurs, partage_tous, limite_vendeur, condition_plus')
      .eq('id', appvendeurId)
      .maybeSingle();
    const { data: acces } = await supabase
      .from('appvendeur_acces')
      .select('idvendeur')
      .eq('idappvendeur', appvendeurId);
    const r = row as { partage_vendeurs?: number; partage_tous?: number; limite_vendeur?: number; condition_plus?: string } | null;
    return {
      shared: Number(r?.partage_vendeurs) === 1,
      all: Number(r?.partage_tous) !== 0,
      vendorIds: (acces as { idvendeur: number }[] | null)?.map((a) => Number(a.idvendeur)) || [],
      limiteVendeur: Number(r?.limite_vendeur || 0),
      conditionPlus: (r?.condition_plus || '').trim(),
    };
  } catch {
    return { shared: false, all: true, vendorIds: [], limiteVendeur: 0, conditionPlus: '' };
  }
}

/**
 * Enregistre le partage d'une app :
 *  - shared=false → retirée des vendeurs
 *  - shared=true & all=true → partagée avec tous
 *  - shared=true & all=false → seuls les vendeurs listés y ont accès
 */
export async function setAppSharing(params: {
  appvendeurId: number;
  marketplaceId: number;
  shared: boolean;
  all: boolean;
  vendorIds: number[];
  limiteVendeur?: number;
  conditionPlus?: string;
}): Promise<void> {
  const { appvendeurId, marketplaceId, shared, all, vendorIds } = params;
  const limite = Number.isFinite(params.limiteVendeur) && (params.limiteVendeur ?? 0) > 0 ? Math.floor(params.limiteVendeur as number) : 0;
  const { error } = await supabase
    .from('appvendeur')
    .update({
      partage_vendeurs: shared ? 1 : 0,
      partage_tous: all ? 1 : 0,
      limite_vendeur: limite,
      condition_plus: (params.conditionPlus || '').trim() || null,
    })
    .eq('id', appvendeurId);
  if (error) throw error;

  // Réinitialise la sélection puis réinsère les vendeurs choisis.
  await supabase.from('appvendeur_acces').delete().eq('idappvendeur', appvendeurId);

  if (shared && !all && vendorIds.length > 0) {
    const unique = [...new Set(vendorIds.filter((n) => Number.isFinite(n) && n > 0))];
    if (unique.length > 0) {
      await supabase.from('appvendeur_acces').insert(
        unique.map((idvendeur) => ({
          idappvendeur: appvendeurId,
          idcommerce: marketplaceId,
          idvendeur,
        })),
      );
    }
  }
}

/**
 * Définit la limite d'utilisation par vendeur (0 = illimité) et la condition
 * à remplir pour obtenir davantage, pour une app partagée aux vendeurs.
 */
export async function updateAppLimits(params: {
  appvendeurId: number;
  limiteVendeur: number;
  conditionPlus: string;
}): Promise<void> {
  const limite = Number.isFinite(params.limiteVendeur) && params.limiteVendeur > 0 ? Math.floor(params.limiteVendeur) : 0;
  const { error } = await supabase
    .from('appvendeur')
    .update({ limite_vendeur: limite, condition_plus: params.conditionPlus.trim() || null })
    .eq('id', params.appvendeurId);
  if (error) throw error;
}

/** Apps partagées à un vendeur donné (global ou sélection nominative). */
export async function fetchVendorSharedApps(
  marketplaceId: number,
  vendorId: number,
): Promise<SharedApp[]> {
  if (!marketplaceId || !vendorId) return [];
  try {
    const { data: installed, error } = await supabase
      .from('appvendeur')
      .select('id, idapp, nomapp, nompage, status, partage_vendeurs, partage_tous, limite_vendeur, condition_plus')
      .eq('idcommerce', marketplaceId)
      .eq('partage_vendeurs', 1)
      .eq('status', 1);
    if (error || !installed) return [];
    const rows = installed as AppvendeurRow[];
    if (rows.length === 0) return [];

    const { data: acces } = await supabase
      .from('appvendeur_acces')
      .select('idappvendeur')
      .eq('idvendeur', vendorId);
    const granted = new Set((acces as { idappvendeur: number }[] | null)?.map((a) => Number(a.idappvendeur)) || []);

    const allowed = rows.filter((r) => Number(r.partage_tous) === 1 || granted.has(Number(r.id)));
    if (allowed.length === 0) return [];

    const appIds = [...new Set(allowed.map((r) => Number(r.idapp)))];
    const { data: apps } = await supabase
      .from('appstore')
      .select('id, nom, description, nompage, image')
      .in('id', appIds);
    const byId = new Map<number, AppstoreRow>();
    (apps as AppstoreRow[] | null)?.forEach((a) => byId.set(Number(a.id), a));

    return allowed.map((r) => {
      const meta = byId.get(Number(r.idapp));
      const nompage = r.nompage || meta?.nompage || '';
      return {
        appvendeurId: Number(r.id),
        idapp: Number(r.idapp),
        nom: meta?.nom || r.nomapp || `App #${r.idapp}`,
        description: meta?.description || '',
        nompage,
        image: meta?.image || '',
        icon: appIconFor(nompage),
        tous: Number(r.partage_tous) === 1,
        limiteVendeur: Number(r.limite_vendeur || 0),
        conditionPlus: (r.condition_plus || '').trim(),
      };
    });
  } catch {
    return [];
  }
}